import axios from "axios";
import Cookies from "js-cookie";
import { clearSessionCookies } from "./sessionCookies";

/**
 * How every API request carries the session, and what happens when the server
 * says the session is over.
 *
 * The session is an HttpOnly cookie that the API sets at sign-in. No script can
 * read it — which is the point: the token used to sit in an ordinary cookie that
 * any script on the page could copy, good for seven days. So nothing here
 * attaches a token. Instead:
 *
 *  - withCredentials, so the browser sends that cookie to the API's host;
 *  - X-Requested-With, which the API requires alongside the cookie. A page on
 *    another site cannot add a custom header without a CORS preflight, and CORS
 *    admits only Youro's origins, so this is what stops another site from using
 *    the cookie (cross-site request forgery). See the server's SessionCookie.
 *
 * Imported for its side effects from index.js, before anything renders.
 */

// Only Youro's own API gets the cookie and the header. Anything else axios is
// ever pointed at should get neither: a credentialed request to another host
// fails CORS, and the header would force a preflight it may not answer.
const isYouroApi = (url = "") => url.includes("/youro/api/");

axios.interceptors.request.use((config) => {
  if (isYouroApi(config.url)) {
    config.withCredentials = true;
    config.headers = config.headers || {};
    config.headers["X-Requested-With"] = "XMLHttpRequest";
  }
  return config;
});

/**
 * Pulls the message out of an error body without assuming one error shape. The
 * API answers with two: ErrorResponse ({error, errorDescription}) from the JWT
 * filter, and Spring's ProblemDetail ({detail, description}) from the exception
 * handler.
 */
const errorText = (error) => {
  const body = error?.response?.data;
  if (!body) return "";
  if (typeof body === "string") return body.toLowerCase();
  return [body.errorDescription, body.description, body.detail, body.message, body.error]
    .filter((part) => typeof part === "string")
    .join(" ")
    .toLowerCase();
};

/**
 * The server saying the *session* is no good — expired, signed out elsewhere,
 * or missing — as opposed to refusing this one request. Only the body tells
 * these apart: an AccessGuard refusal for somebody else's record is also a 4xx,
 * and must not sign anyone out.
 */
const SESSION_REJECTED_MESSAGES = [
  "missing jwt token",
  "invalid jwt token",
  "jwt signature is invalid",
  "jwt token has expired",
];

const serverRejectedSession = (error) =>
  SESSION_REJECTED_MESSAGES.some((message) => errorText(error).includes(message));

const endSession = () => {
  // The server has already ended it; this only tidies the browser's side.
  clearSessionCookies();
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign("/login");
  }
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // The account id is kept in a readable cookie beside the session for the
    // screens' use. If there is none, nobody thought they were signed in, so a
    // "missing token" answer is expected and nothing needs ending.
    const thinksSignedIn = Boolean(Cookies.get("U_ID"));

    if (thinksSignedIn && (status === 401 || status === 403) && serverRejectedSession(error)) {
      endSession();
    }

    return Promise.reject(error);
  }
);
